'use client';

import {RecaptchaScript} from '@gfiber/third-parties/google';
import {zodResolver} from '@hookform/resolvers/zod';
import {useParams} from 'next/navigation';
import type React from 'react';
import {useId} from 'react';
import {Controller, type SubmitHandler, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useSubmit} from '@/app/forms/_hooks/useSubmit';
import {REGISTRY} from '@/app/forms/_registry/client';
import * as constants from '@/app/forms/constants';
import {PROPERTY_TYPES_MAP} from '@/app/forms/i18n';
import {ApiError, type InferFormValues} from '@/app/forms/types';
import {
  FileUpload,
  FormError,
  SelectField,
  SelectItem,
  TextArea,
  TextField,
} from '@/components';
import {isRequired, maxLength} from '@/lib/utils/common';
import {
  getRootError,
  getSchemaShape,
  getSfdcLanguage,
  handleError,
  onSubmitError,
  tSafe,
} from '../../utils';
import {Consent} from '../Consent/Consent';
import {Description, FormDescription} from '../Description/Description';
import {SubmitButton} from '../SubmitButton/SubmitButton';
import {SuccessMessage} from '../SuccessMessage/SuccessMessage';

function ContactSalesForm(): React.JSX.Element {
  const {t} = useTranslation();
  const config = REGISTRY.ContactSalesForm;
  type ContactSalesFormData = InferFormValues<typeof config>;
  const params = useParams();

  const {
    control,
    formState: {errors, isSubmitted, isValid},
    handleSubmit,
    setError,
  } = useForm<ContactSalesFormData>({
    resolver: zodResolver(config.schema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      street: '',
      apt: '',
      zipCode: '',
      description: '',
      phoneNumber: '',
      propertyType: '',
      attachments: [],
      formType: 'Sales',
      language: getSfdcLanguage(params?.locale),
    },
  });

  const fields = getSchemaShape(config.schema);
  const {mutate, isPending, isSuccess} = useSubmit<ContactSalesFormData>(
    config.id,
  );
  const id = useId();

  const onSubmit: SubmitHandler<ContactSalesFormData> = async data => {
    const recaptchaToken = await window.grecaptcha?.enterprise.execute(
      constants.RECAPTCHA_SITE_KEY,
      {action: config.recaptchaAction || 'submit_general_form'},
    );

    if (!recaptchaToken) {
      handleError(
        new ApiError(t('errors.recaptcha'), {
          code: 'RECAPTCHA_FAILED',
          message: 'reCAPTCHA token could not be obtained',
        }),
        setError,
        fields,
      );
      return;
    }

    mutate(
      {data, recaptchaToken},
      {
        onError: error => {
          handleError(error, setError, fields);
        },
      },
    );
  };

  return (
    <>
      <h1 className="title">{t(config.title)}</h1>
      {isSuccess ? (
        <SuccessMessage />
      ) : (
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
          noValidate
        >
          <fieldset disabled={isPending}>
            <FormDescription>
              <Description>
                <Trans
                  t={t}
                  i18nKey="forms.contactSales.description"
                >
                  <a
                    href={constants.HELP_CENTER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Help Center
                  </a>
                </Trans>
              </Description>
            </FormDescription>
            <div className="field">
              <Controller
                control={control}
                name="firstName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.contactSales.firstName')}
                    id={`firstName-${id}`}
                    required={isRequired(fields.firstName)}
                    errorText={tSafe(errors.firstName?.message, t)}
                    maxLength={maxLength(fields.firstName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="lastName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.contactSales.lastName')}
                    id={`lastName-${id}`}
                    required={isRequired(fields.lastName)}
                    errorText={tSafe(errors.lastName?.message, t)}
                    maxLength={maxLength(fields.lastName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="email"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="email"
                    label={t('forms.contactSales.email')}
                    id={`email-${id}`}
                    required={isRequired(fields.email)}
                    errorText={tSafe(errors.email?.message, t)}
                    maxLength={maxLength(fields.email)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="street"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.contactSales.street')}
                    id={`street-${id}`}
                    required={isRequired(fields.street)}
                    errorText={tSafe(errors.street?.message, t)}
                    maxLength={maxLength(fields.street)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="apt"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.contactSales.apt')}
                    id={`apt-${id}`}
                    required={isRequired(fields.apt)}
                    errorText={tSafe(errors.apt?.message, t)}
                    maxLength={maxLength(fields.apt)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="zipCode"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.contactSales.zipCode')}
                    id={`zipCode-${id}`}
                    required={isRequired(fields.zipCode)}
                    errorText={tSafe(errors.zipCode?.message, t)}
                    maxLength={maxLength(fields.zipCode)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="phoneNumber"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.contactSales.phoneNumber')}
                    id={`phoneNumber-${id}`}
                    required={isRequired(fields.phoneNumber)}
                    errorText={tSafe(errors.phoneNumber?.message, t)}
                    maxLength={maxLength(fields.phoneNumber)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="propertyType"
                control={control}
                render={({field, fieldState: {error}}) => (
                  <SelectField
                    label={t('forms.contactSales.propertyType')}
                    required={isRequired(fields.propertyType)}
                    value={field.value ?? undefined}
                    placeholder={t('forms.shared.selectOne')}
                    invalid={!!error}
                    onChange={field.onChange}
                    errorText={tSafe(errors.propertyType?.message, t)}
                  >
                    {PROPERTY_TYPES_MAP.map(item => (
                      <SelectItem
                        key={item.id}
                        value={item.value}
                      >
                        {t(item.i18nKey, {defaultValue: item.id})}
                      </SelectItem>
                    ))}
                  </SelectField>
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="description"
                render={({field}) => (
                  <TextArea
                    {...field}
                    label={t('forms.contactSales.issueDescription')}
                    id={`description-${id}`}
                    required={isRequired(fields.description)}
                    errorText={tSafe(errors.description?.message, t)}
                    maxLength={maxLength(fields.description)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                name="attachments"
                control={control}
                render={({field}) => (
                  <>
                    <FileUpload
                      value={field.value ?? []}
                      instruction={t(
                        'forms.contactSales.attachments.instruction',
                      )}
                      buttonLabel={t('fileUpload.buttonLabel')}
                      defaultFileName={t('fileUpload.defaultFileName')}
                      fileAttachedLabel={t('fileUpload.fileAttachedLabel')}
                      maxFileSizeErrorMessage={t('errors.fileTooBig', {
                        size: constants.MAX_FILE_SIZE_MB,
                      })}
                      fileTypeErrorMessage={t('errors.invalidFileType')}
                      accept={constants.ACCEPTED_FILE_TYPES_STRING}
                      maxFiles={5}
                      onFileSelect={(_id, files) => field.onChange(files)}
                    />
                    <FormError
                      message={tSafe(errors.attachments?.message, t)}
                    />
                  </>
                )}
              />
            </div>
            <SubmitButton
              isDisabled={isPending}
              isPending={isPending}
              rootError={getRootError(errors, isSubmitted, isValid, t)}
            />
            <Consent />
          </fieldset>
          {constants.RECAPTCHA_SITE_KEY && (
            <RecaptchaScript siteKey={constants.RECAPTCHA_SITE_KEY} />
          )}
        </form>
      )}
    </>
  );
}

export {ContactSalesForm};
